// GateForm.tsx - Updated to match OrganizationForm modal design + Help Panel
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './GateManagement.css';
import '../companyManagement/CompanyFormOverrides.css';
import { GateUserSelection } from './GateUserSelection';
import { GateApiService } from './apiService';
import { Gate, GateUser, GateFormData, Region, GateManagementProps } from './types';

interface GateFormProps extends GateManagementProps {
  editingGate: Gate | null;
  regions: Region[];
  selectedRegion: Region | null;
  onBack: () => void;
  onSuccess: () => void;
}

// ─── Help Panel Content ─────────────────────────────────────────────
interface HelpItem {
  title: string;
  description: string;
  tip?: string;
}

const HELP_CONTENT: Record<string, HelpItem> = {
  default: {
    title: 'Gate Configuration',
    description: 'Use this form to create or update a gate entry point within a region. Gates define the physical access points through which personnel and vehicles enter the facility.',
    tip: 'All fields marked with * are required. Click on any field to see specific guidance.'
  },
  gateNumber: {
    title: 'Gate Number',
    description: 'Enter a unique identifier for this gate. This will appear on gate passes and be used by security personnel to identify the entry point.',
    tip: 'Use a clear naming convention such as "Gate-1", "North Gate", or "Main Entrance".'
  },
  regionId: {
    title: 'Region',
    description: 'The region this gate belongs to. All gate pass requests routed through this gate will follow the region\'s workflow rules.',
    tip: 'If you accessed this form from a specific region, the region is pre-selected and cannot be changed.'
  },
  description: {
    title: 'Description',
    description: 'Add any notes about this gate, such as its location, operating hours, or special access requirements.',
    tip: 'This is optional but useful for security personnel and administrators.'
  },
  accessCodes: {
    title: 'Access Codes',
    description: 'Define alphanumeric access codes associated with this gate. These codes are used for security verification when personnel pass through the gate.',
    tip: 'Enter comma-separated codes (e.g. A1, B2, C3). Only letters and numbers are allowed.'
  },
  isActive: {
    title: 'Active Status',
    description: 'Controls whether this gate is available for selection in gate pass request forms. Inactive gates will not appear as options when users create new requests.',
    tip: 'Deactivate a gate temporarily if it is under maintenance or closed, without deleting it.'
  },
  copyFromExisting: {
    title: 'Copy from Existing Gate',
    description: 'Quickly assign approvers and reviewers by copying them from another gate in the same region. You can modify the copied selections before saving.',
    tip: 'This is useful when setting up a new gate with a similar workflow to an existing one.'
  },
  approvers: {
    title: 'Gate Approvers',
    description: 'Assign users who are authorised to approve or reject gate pass requests routed to this gate. Only users with the "Gate / Asset Approver" role are available for selection — roles can be assigned via "Manage Users".',
    tip: 'If no users appear, ensure there are users with the Gate / Asset Approver role assigned.'
  },
  reviewers: {
    title: 'Gate Reviewers',
    description: 'Assign users who can review and provide feedback on gate pass requests before final gate-level approval. Only users with the "Gate / Asset Reviewer" role are available for selection.',
    tip: 'Reviewers cannot make final approval decisions — they can only endorse or flag concerns.'
  }
};

export const GateForm: React.FC<GateFormProps> = ({
  editingGate,
  regions,
  selectedRegion,
  getAccessToken,
  onStatusUpdate,
  onBack,
  onSuccess
}) => {
  const [formData, setFormData] = useState<GateFormData>({
    gateNumber: '',
    regionId: '',
    isActive: true,
    description: '',
    accessCodes: '',
    approverIds: [],
    reviewerIds: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accessCodesError, setAccessCodesError] = useState<string>('');
  const [gateUsers, setGateUsers] = useState<GateUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  // ─── Role-filtered user lists ──────────────────────────────────────
  const userHasRole = (user: GateUser, targetRole: string): boolean => {
    if (!user.role) return false;
    const roles = user.role.split(',').map(r => r.trim().toLowerCase());
    return roles.includes(targetRole.toLowerCase());
  };

  // Only show users with 'gate_approver' role in the approvers selector
  const approverEligibleUsers = useMemo(() => {
    return gateUsers.filter(u => userHasRole(u, 'gate_approver'));
  }, [gateUsers]);

  // Only show users with 'gate_reviewer' role in the reviewers selector
  const reviewerEligibleUsers = useMemo(() => {
    return gateUsers.filter(u => userHasRole(u, 'gate_reviewer'));
  }, [gateUsers]);
  
  // Copy functionality state
  const [copyFromExisting, setCopyFromExisting] = useState(false);
  const [availableGates, setAvailableGates] = useState<Gate[]>([]);
  const [selectedSourceGate, setSelectedSourceGate] = useState<number | null>(null);
  const [isLoadingGates, setIsLoadingGates] = useState(false);
  
  const [apiService] = useState(() => new GateApiService(getAccessToken));
  const [focusedField, setFocusedField] = useState<string>('default');
  const [showHelp, setShowHelp] = useState<boolean>(true);

  // Load gate users
  const loadGateUsers = useCallback(async () => {
    console.log('📡 GateForm - loadGateUsers called');
    setIsLoadingUsers(true);
    
    const result = await apiService.loadGateUsers();
    console.log('📡 GateForm - loadGateUsers response:', result);
    
    if (result.success && result.data) {
      console.log('✅ GateForm - Setting gateUsers:', result.data.length, 'users');
      console.log('   First user sample:', result.data[0]);
      setGateUsers(result.data);
    } else {
      console.log('❌ GateForm - Failed to load users:', result.message);
      setGateUsers([]);
      onStatusUpdate({
        type: 'error',
        message: result.message || 'Failed to load gate users'
      });
    }
    
    setIsLoadingUsers(false);
  }, [apiService, onStatusUpdate]);

  // Load available gates for copying
  const loadAvailableGates = useCallback(async (regionId: number) => {
    if (!regionId) {
      setAvailableGates([]);
      return;
    }

    setIsLoadingGates(true);
    
    const result = await apiService.loadGates(regionId);
    
    if (result.success && result.data) {
      const filteredGates = result.data.filter(gate => 
        gate.gateId !== editingGate?.gateId
      );
      setAvailableGates(filteredGates);
    } else {
      setAvailableGates([]);
      onStatusUpdate({
        type: 'error',
        message: result.message || 'Failed to load gates'
      });
    }
    
    setIsLoadingGates(false);
  }, [apiService, onStatusUpdate, editingGate?.gateId]);

  // Initialize form data when editing or when selected region changes
  useEffect(() => {
    if (editingGate) {
      setFormData({
        gateNumber: editingGate.gateNumber || '',
        regionId: editingGate.regionId,
        isActive: !!editingGate.isActive,
        description: editingGate.description || '',
        accessCodes: editingGate.accessCodes || '',
        approverIds: editingGate.approvers?.map(a => a.userId) || [],
        reviewerIds: editingGate.reviewers?.map(r => r.userId) || []
      });
    } else if (selectedRegion) {
      setFormData(prev => ({
        ...prev,
        regionId: selectedRegion.regionId
      }));
    }
  }, [editingGate, selectedRegion]);

  // Load gate users on component mount
  useEffect(() => {
    loadGateUsers();
  }, [loadGateUsers]);

  // Debug: Monitor gateUsers changes
  useEffect(() => {
    console.log('📊 GateForm - gateUsers state changed:', {
      count: gateUsers.length,
      sample: gateUsers[0]
    });
  }, [gateUsers]);

  // Debug: Monitor formData changes
  useEffect(() => {
    console.log('📝 GateForm - formData changed:', {
      approverIds: formData.approverIds,
      reviewerIds: formData.reviewerIds
    });
  }, [formData.approverIds, formData.reviewerIds]);

  // Load gates when copy toggle is enabled or region changes
  useEffect(() => {
    if (copyFromExisting && formData.regionId) {
      loadAvailableGates(Number(formData.regionId));
    }
  }, [copyFromExisting, formData.regionId, loadAvailableGates]);

  // Validate access codes - comma-separated alphanumeric only
  const validateAccessCodes = (value: string): string => {
    if (!value.trim()) return '';
    const codes = value.split(',').map(c => c.trim()).filter(c => c);
    const invalidCodes = codes.filter(c => !/^[a-zA-Z0-9]+$/.test(c));
    if (invalidCodes.length > 0) {
      return `Invalid codes: "${invalidCodes.join(', ')}". Only alphanumeric values allowed (letters and numbers).`;
    }
    return '';
  };

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    if (name === 'accessCodes') {
      const sanitized = value.replace(/[^a-zA-Z0-9, ]/g, '');
      setFormData(prev => ({
        ...prev,
        accessCodes: sanitized
      }));
      setAccessCodesError(validateAccessCodes(sanitized));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle copy from existing toggle
  const handleCopyToggle = (enabled: boolean) => {
    setCopyFromExisting(enabled);
    setSelectedSourceGate(null);
    
    if (!enabled) {
      setFormData(prev => ({
        ...prev,
        approverIds: [],
        reviewerIds: []
      }));
    }
  };

  // Handle source gate selection for copying
  const handleSourceGateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const gateId = parseInt(e.target.value) || null;
    setSelectedSourceGate(gateId);
    
    if (gateId) {
      const sourceGate = availableGates.find(g => g.gateId === gateId);
      if (sourceGate) {
        setFormData(prev => ({
          ...prev,
          approverIds: sourceGate.approvers?.map(a => a.userId) || [],
          reviewerIds: sourceGate.reviewers?.map(r => r.userId) || []
        }));
        
        onStatusUpdate({
          type: 'success',
          message: `Copied ${sourceGate.approvers?.length || 0} approvers and ${sourceGate.reviewers?.length || 0} reviewers from "${sourceGate.gateNumber}"`
        });
      }
    } else {
      setFormData(prev => ({
        ...prev,
        approverIds: [],
        reviewerIds: []
      }));
    }
  };

  // Handle approver/reviewer selection changes
  const handleApproversChange = (selectedIds: number[]) => {
    console.log('🎯 GateForm - handleApproversChange called');
    console.log('  - New selection:', selectedIds);
    console.log('  - Current approverIds:', formData.approverIds);
    
    setFormData(prev => ({
      ...prev,
      approverIds: selectedIds
    }));
    
    console.log('  ✅ State update triggered');
  };

  const handleReviewersChange = (selectedIds: number[]) => {
    console.log('🎯 GateForm - handleReviewersChange called');
    console.log('  - New selection:', selectedIds);
    console.log('  - Current reviewerIds:', formData.reviewerIds);
    
    setFormData(prev => ({
      ...prev,
      reviewerIds: selectedIds
    }));
    
    console.log('  ✅ State update triggered');
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.accessCodes) {
      const error = validateAccessCodes(formData.accessCodes);
      if (error) {
        setAccessCodesError(error);
        onStatusUpdate({
          type: 'error',
          message: 'Please fix the access codes validation error before submitting.'
        });
        return;
      }
    }
    
    setIsSubmitting(true);

    try {
      let result;
      if (editingGate) {
        result = await apiService.updateGate(editingGate.gateId, formData);
      } else {
        result = await apiService.createGate(formData);
      }

      if (result.success) {
        onStatusUpdate({
          type: 'success',
          message: result.message || 'Operation completed successfully'
        });
        onSuccess();
      } else {
        // Sanitize technical error messages before displaying to user
        let userMessage = result.message || 'Operation failed';
        if (/UNIQUE|unique.*key|duplicate.*key|constraint|violation/i.test(userMessage)) {
          userMessage = `A gate named "${formData.gateNumber}" already exists in this region. Please use a different name.`;
        } else if (/SQL|MSSQL|transaction|deadlock|timeout/i.test(userMessage)) {
          userMessage = 'A server error occurred. Please try again or contact support if the issue persists.';
        }
        onStatusUpdate({
          type: 'error',
          message: userMessage
        });
      }
    } catch (error) {
      console.error('Submit error:', error);
      onStatusUpdate({
        type: 'error',
        message: 'An unexpected error occurred. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    setFormData({
      gateNumber: '',
      regionId: selectedRegion?.regionId || '',
      isActive: true,
      description: '',
      accessCodes: '',
      approverIds: [],
      reviewerIds: []
    });
    setAccessCodesError('');
    setCopyFromExisting(false);
    setSelectedSourceGate(null);
    onBack();
  };

  // Get current help content based on focused field
  const currentHelp = HELP_CONTENT[focusedField] || HELP_CONTENT.default;

  return (
    <div className="modal-overlay">
      <div className={`modal-container ${showHelp ? 'company-form-with-help' : ''}`}>
        <div className="modal-header">
          <h2 className="modal-title">
            {editingGate ? 'Edit Gate' : 'Create Gate'}
            {selectedRegion && (
              <span style={{ fontSize: '14px', fontWeight: '400', opacity: '0.8' }}>
                {' '}- {selectedRegion.regionName}
              </span>
            )}
          </h2>
          <button 
            type="button" 
            className="modal-close-btn"
            onClick={handleClose}
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        <div className="modal-content-with-help">
          {/* ─── Left: Form ─────────────────────────────────────────── */}
          <form onSubmit={handleSubmit} className="modal-form company-form-main">
            <div className="modal-body">
              {/* Gate Details Section */}
              <div className="form-field" onFocus={() => setFocusedField('gateNumber')}>
                <label htmlFor="gateNumber" className="field-label">
                  Gate Number <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="gateNumber"
                  name="gateNumber"
                  value={formData.gateNumber}
                  onChange={handleInputChange}
                  placeholder="e.g., Gate-1, North Gate, Main Entrance"
                  className="field-input"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-field" onFocus={() => setFocusedField('regionId')}>
                <label htmlFor="regionId" className="field-label">
                  Region <span className="required">*</span>
                </label>
                {selectedRegion && !editingGate ? (
                  <div style={{ 
                    padding: '12px 16px', 
                    background: '#f8f9fa', 
                    border: '1px solid #e9ecef', 
                    borderRadius: '4px',
                    color: '#495057'
                  }}>
                    <strong>{selectedRegion.regionName}</strong>
                    {selectedRegion.regionCode && <span> ({selectedRegion.regionCode})</span>}

                    <input
                      type="hidden"
                      name="regionId"
                      value={selectedRegion.regionId}
                    />
                  </div>
                ) : (
                  <select
                    id="regionId"
                    name="regionId"
                    value={formData.regionId}
                    onChange={handleInputChange}
                    className="field-select"
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Select a region</option>
                    {regions.map(region => (
                      <option key={region.regionId} value={region.regionId}>
                        {region.regionName} {region.regionCode && `(${region.regionCode})`}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="form-field" onFocus={() => setFocusedField('description')}>
                <label htmlFor="description" className="field-label">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Notes about this gate"
                  className="field-textarea"
                  rows={3}
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-field" onFocus={() => setFocusedField('accessCodes')}>
                <label htmlFor="accessCodes" className="field-label">
                  Access Codes
                </label>
                <input
                  type="text"
                  id="accessCodes"
                  name="accessCodes"
                  value={formData.accessCodes}
                  onChange={handleInputChange}
                  placeholder="e.g., A1, B2, C3 (comma-separated alphanumeric codes)"
                  className={`field-input ${accessCodesError ? 'field-input-error' : ''}`}
                  disabled={isSubmitting}
                />
                {accessCodesError ? (
                  <small style={{ display: 'block', marginTop: '4px', color: '#dc3545', fontSize: '12px' }}>
                    {accessCodesError}
                  </small>
                ) : (
                  <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '12px' }}>
                    Enter comma-separated alphanumeric codes (e.g., 1, 2, 3 or ABC, DEF, GH1)
                  </small>
                )}
              </div>

              <div className="form-checkboxes" onClick={() => setFocusedField('isActive')}>
                <div className="checkbox-field">
                  <input
                    type="checkbox"
                    id="isActive"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    className="field-checkbox"
                    disabled={isSubmitting}
                  />
                  <label htmlFor="isActive" className="checkbox-label">
                    Active (Allow selection in Gate Pass form)
                  </label>
                </div>
              </div>

              {/* Gate Workflow Assignment Section */}
              {formData.regionId && (
                <>
                  <div style={{ 
                    margin: '32px 0 24px 0', 
                    paddingTop: '24px', 
                    borderTop: '1px solid #e9ecef' 
                  }}>
                    <h3 style={{ 
                      margin: '0 0 16px 0', 
                      fontSize: '16px', 
                      fontWeight: '600', 
                      color: '#374151' 
                    }}>
                      Gate Workflow Assignment
                    </h3>
                    <p style={{ 
                      margin: '0 0 20px 0', 
                      fontSize: '14px', 
                      color: '#6b7280', 
                      lineHeight: '1.5' 
                    }}>
                      Assign users who will be responsible for approving and reviewing gate pass requests for this gate. Any non-blacklisted user can be assigned.
                    </p>
                  </div>

                  {/* Copy from Existing Gate Option */}
                  <div className="form-field" onClick={() => setFocusedField('copyFromExisting')}>
                    <div className="checkbox-field">
                      <input
                        type="checkbox"
                        id="copyFromExisting"
                        checked={copyFromExisting}
                        onChange={(e) => handleCopyToggle(e.target.checked)}
                        className="field-checkbox"
                        disabled={isSubmitting || isLoadingGates}
                      />
                      <label htmlFor="copyFromExisting" className="checkbox-label">
                        Copy approvers and reviewers from an existing gate in this region
                      </label>
                    </div>
                    
                    {copyFromExisting && (
                      <div style={{ marginTop: '16px' }}>
                        <label htmlFor="sourceGate" className="field-label">
                          Select Source Gate
                        </label>
                        <select
                          id="sourceGate"
                          value={selectedSourceGate || ''}
                          onChange={handleSourceGateChange}
                          className="field-select"
                          disabled={isSubmitting || isLoadingGates}
                        >
                          <option value="">Choose gate to copy from...</option>
                          {availableGates.map(gate => (
                            <option key={gate.gateId} value={gate.gateId}>
                              {gate.gateNumber} - 
                              {gate.approvers?.length || 0} approvers, {gate.reviewers?.length || 0} reviewers
                            </option>
                          ))}
                        </select>
                        {isLoadingGates && (
                          <small style={{ display: 'block', marginTop: '8px', color: '#6b7280' }}>
                            Loading available gates...
                          </small>
                        )}
                        {!isLoadingGates && availableGates.length === 0 && (
                          <small style={{ display: 'block', marginTop: '8px', color: '#6b7280' }}>
                            No existing gates found in this region to copy from
                          </small>
                        )}
                        {selectedSourceGate && (
                          <small style={{ display: 'block', marginTop: '8px', color: '#059669' }}>
                            Approvers and reviewers have been copied. You can modify them below before saving.
                          </small>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Gate User Selection Components */}
                  
<div
  style={{ marginTop: '24px', position: 'relative', zIndex: 2 }}
  onClick={() => setFocusedField('approvers')}
>
  <GateUserSelection
    users={approverEligibleUsers}
    selectedUserIds={formData.approverIds}
    onSelectionChange={handleApproversChange}
    label="Gate Approvers"
    placeholder="Search for gate approvers by name or email..."
    isLoading={isLoadingUsers}
    disabled={isSubmitting}
  />
</div>

<div
  style={{ marginTop: '20px', position: 'relative', zIndex: 1 }}
  onClick={() => setFocusedField('reviewers')}
>
  <GateUserSelection
    users={reviewerEligibleUsers}
    selectedUserIds={formData.reviewerIds}
    onSelectionChange={handleReviewersChange}
    label="Gate Reviewers"
    placeholder="Search for gate reviewers by name or email..."
    isLoading={isLoadingUsers}
    disabled={isSubmitting}
  />
</div>

                  <div style={{ 
                    marginTop: '16px', 
                    padding: '12px', 
                    background: '#f8f9fa', 
                    borderRadius: '4px',
                    fontSize: '13px',
                    color: '#6b7280',
                    lineHeight: '1.5'
                  }}>
                    <strong>Note:</strong> Approvers have the authority to approve or reject gate pass requests for this gate. 
                    Reviewers can review and provide feedback but cannot make final approval decisions. Only users with the matching role (Gate / Asset Approver or Gate / Asset Reviewer) are shown — assign roles via "Manage Users".
                    {copyFromExisting && (
                      <> You can modify the copied selections above before saving.</>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button 
                type="button" 
                className="btn-cancel"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn-submit"
                disabled={isSubmitting || isLoadingUsers}
              >
                {isSubmitting ? (
                  editingGate ? 'Updating...' : 'Creating...'
                ) : (
                  editingGate ? 'Update Gate' : 'Create Gate'
                )}
              </button>
            </div>
          </form>

          {/* ─── Help Panel (collapsible with animation) ────────────── */}
          <div className={`company-help-panel ${showHelp ? 'help-panel-open' : 'help-panel-closed'}`}>
            <div className="help-panel-inner">
              <div className={`help-panel-card ${focusedField !== 'default' ? 'help-active' : ''}`}>
                <h4 className="help-card-title">{currentHelp.title}</h4>
                <p className="help-card-description">{currentHelp.description}</p>
                {currentHelp.tip && (
                  <div className="help-card-tip">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F3694A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                    <span>{currentHelp.tip}</span>
                  </div>
                )}
              </div>

              <div className="help-panel-quickref">
                <h4 className="help-quickref-title">Quick Reference</h4>
                <ul className="help-quickref-list">
                  <li>
                    <span className="help-quickref-bullet"></span>
                    <span>Access codes must be alphanumeric, comma-separated</span>
                  </li>
                  <li>
                    <span className="help-quickref-bullet"></span>
                    <span>Inactive gates are hidden from gate pass forms</span>
                  </li>
                  <li>
                    <span className="help-quickref-bullet"></span>
                    <span>Copy approvers/reviewers from existing gates to save time</span>
                  </li>
                  <li>
                    <span className="help-quickref-bullet"></span>
                    <span>Only users with matching roles appear in selection</span>
                  </li>
                  <li>
                    <span className="help-quickref-bullet"></span>
                    <span>Approvers have final authority; reviewers provide feedback only</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Toggle strip — outer (right) edge */}
          <div className="help-toggle-strip">
            <button
              type="button"
              className="help-toggle-icon-btn"
              onClick={() => setShowHelp(!showHelp)}
              title={showHelp ? 'Hide Guide' : 'Show Guide'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </button>
            <button
              type="button"
              className="help-toggle-tab"
              onClick={() => setShowHelp(!showHelp)}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                {showHelp
                  ? <polyline points="9 18 15 12 9 6"/>
                  : <polyline points="15 18 9 12 15 6"/>
                }
              </svg>
              <span>{showHelp ? 'Hide Guide' : 'Show Guide'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};